import FormatBoldRoundedIcon from "@mui/icons-material/FormatBoldRounded";
import FormatItalicRoundedIcon from "@mui/icons-material/FormatItalicRounded";
import FormatUnderlinedRoundedIcon from "@mui/icons-material/FormatUnderlinedRounded";
import TitleRoundedIcon from "@mui/icons-material/TitleRounded";
import FormatListNumberedRoundedIcon from "@mui/icons-material/FormatListNumberedRounded";
import FormatListBulletedRoundedIcon from "@mui/icons-material/FormatListBulletedRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import FormatAlignLeftRoundedIcon from "@mui/icons-material/FormatAlignLeftRounded";
import FormatAlignCenterRoundedIcon from "@mui/icons-material/FormatAlignCenterRounded";
import FormatAlignRightRoundedIcon from "@mui/icons-material/FormatAlignRightRounded";
import FormatAlignJustifyRoundedIcon from "@mui/icons-material/FormatAlignJustifyRounded";
import FormatColorTextRoundedIcon from "@mui/icons-material/FormatColorTextRounded";
import React, { useEffect, useRef, useState } from "react";
import { Box, IconButton, Menu, MenuItem, Stack, Tooltip } from "@mui/material";
import { narrativeEditorHtml, plainTextHtml, sanitizeNarrativeHtml } from "../utils/narrativeHtml";

export default function RichTextField({ label, value = "", onChange, disabled = false, rows = 5 }) {
  const editor = useRef(null);
  const lastValue = useRef(null);
  const savedSelection = useRef(null);
  const [alignmentAnchor, setAlignmentAnchor] = useState(null);
  const [colorAnchor, setColorAnchor] = useState(null);
  const rememberSelection = () => {
    const selection = window.getSelection();
    if (selection?.rangeCount && editor.current?.contains(selection.getRangeAt(0).commonAncestorContainer)) {
      savedSelection.current = selection.getRangeAt(0).cloneRange();
    }
  };
  useEffect(() => {
    if (editor.current && value !== lastValue.current) {
      editor.current.innerHTML = narrativeEditorHtml(value);
      lastValue.current = value;
    }
  }, [value]);

  const publish = () => {
    const html = sanitizeNarrativeHtml(editor.current.innerHTML);
    lastValue.current = html;
    onChange(html);
  };
  const format = (command, argument = null) => {
    if (disabled) return;
    editor.current.focus();
    const selection = window.getSelection();
    if (savedSelection.current && editor.current.contains(savedSelection.current.commonAncestorContainer)) {
      selection.removeAllRanges();
      selection.addRange(savedSelection.current);
    }
    document.execCommand(command, false, argument);
    rememberSelection();
    publish();
  };
  const insertLink = () => {
    const url = window.prompt("Enter link URL");
    if (url && /^(https?:\/\/|mailto:|tel:)/i.test(url.trim())) format("createLink", url.trim());
  };
  const paste = event => {
    event.preventDefault();
    if (disabled) return;
    const clipboard = event.clipboardData;
    const html = clipboard.getData("text/html");
    const safeHtml = html ? sanitizeNarrativeHtml(html) : plainTextHtml(clipboard.getData("text/plain"));
    const selection = window.getSelection();
    let range = selection?.rangeCount ? selection.getRangeAt(0) : null;
    if (!range || !editor.current.contains(range.commonAncestorContainer)) {
      range = document.createRange();
      range.selectNodeContents(editor.current);
      range.collapse(false);
    }
    range.deleteContents();
    const fragment = range.createContextualFragment(safeHtml);
    const lastNode = fragment.lastChild;
    range.insertNode(fragment);
    if (lastNode) range.setStartAfter(lastNode);
    range.collapse(true);
    selection?.removeAllRanges();
    selection?.addRange(range);
    publish();
  };

  const button = (name, Icon, onClick) => <Tooltip title={name} key={name}>
    <span><IconButton size="small" aria-label={name} disabled={disabled}
      onMouseDown={event => { rememberSelection(); event.preventDefault(); }} onClick={onClick}
      sx={{ width: 26, height: 26, color: "#667085" }}><Icon sx={{ fontSize: 17 }} /></IconButton></span>
  </Tooltip>;

  return <Box>
    <Stack direction="row" spacing={0.25} role="toolbar" aria-label={`${label} formatting`}
      sx={{ justifyContent: "flex-end", alignItems: "center", mb: 0.9, flexWrap: "wrap" }}>
      {[
        ["Bold", FormatBoldRoundedIcon, "bold"],
        ["Italic", FormatItalicRoundedIcon, "italic"],
        ["Underline", FormatUnderlinedRoundedIcon, "underline"],
        ["Heading", TitleRoundedIcon, "formatBlock", "h2"],
        ["Numbered list", FormatListNumberedRoundedIcon, "insertOrderedList"],
        ["Bulleted list", FormatListBulletedRoundedIcon, "insertUnorderedList"],
      ].map(([name, Icon, command, argument]) => button(name, Icon, () => format(command, argument)))}
      {button("Insert link", LinkRoundedIcon, insertLink)}
      {button("Text alignment", FormatAlignLeftRoundedIcon, event => { rememberSelection(); setAlignmentAnchor(event.currentTarget); })}
      {button("Text colour", FormatColorTextRoundedIcon, event => { rememberSelection(); setColorAnchor(event.currentTarget); })}
    </Stack>
    <Menu anchorEl={alignmentAnchor} open={Boolean(alignmentAnchor)} onClose={() => setAlignmentAnchor(null)} disableRestoreFocus>
      {[["Left", "justifyLeft", FormatAlignLeftRoundedIcon], ["Center", "justifyCenter", FormatAlignCenterRoundedIcon],
        ["Right", "justifyRight", FormatAlignRightRoundedIcon], ["Justify", "justifyFull", FormatAlignJustifyRoundedIcon]].map(([name, command, Icon]) =>
        <MenuItem key={command} onMouseDown={event => event.preventDefault()} onClick={() => { setAlignmentAnchor(null); format(command); }} sx={{ gap: 1, fontSize: 13 }}>
          <Icon fontSize="small" />{name}
        </MenuItem>)}
    </Menu>
    <Menu anchorEl={colorAnchor} open={Boolean(colorAnchor)} onClose={() => setColorAnchor(null)} disableRestoreFocus>
      {["#1F2937", "#3451F4", "#7C3AED", "#DC2626", "#D97706", "#059669"].map(color =>
        <MenuItem key={color} aria-label={`Use ${color} text colour`} onMouseDown={event => event.preventDefault()}
          onClick={() => { setColorAnchor(null); format("foreColor", color); }}>
          <Box sx={{ width: 18, height: 18, borderRadius: "50%", bgcolor: color }} />
        </MenuItem>)}
    </Menu>
    <Box ref={editor} role="textbox" aria-label={label} aria-multiline="true" aria-disabled={disabled}
      contentEditable={!disabled} suppressContentEditableWarning tabIndex={disabled ? -1 : 0}
      onInput={() => { rememberSelection(); publish(); }} onMouseUp={rememberSelection} onKeyUp={rememberSelection} onBlur={rememberSelection} onPaste={paste} onDrop={event => event.preventDefault()}
      sx={{ border: "1px solid #D0D5DD", borderRadius: "8px", bgcolor: "white", "&:focus": { borderColor: "#9C4FFF" }, minHeight: rows * 24, px: 1.75, py: 1.4, outline: "none", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", overflowWrap: "anywhere", opacity: disabled ? 0.6 : 1, "& p": { m: 0, mb: 1 }, "& ul, & ol": { pl: 3 } }} />
  </Box>;
}
